(() => {
  if (window.__bpdPanelAddonSyncLoaded) return;
  window.__bpdPanelAddonSyncLoaded = true;

  const MANAGED = '_bpd_panel_addon';
  const SOURCE = '_bpd_source_panel_variant';
  const OVERRIDE = '_bpd_manual_override';
  const KIT_HANDLE = 'panel-installation-kits';
  const EVENT_NAMES = ['cart:update','cart:reload','cart:count:update','cart:refresh','cart:updated','theme:cart:change','theme:loading:done','variant:add'];

  let timer = 0;
  let syncing = false;
  let rerun = false;
  let kitVariantsPromise = null;
  let renderedSections = {};

  const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const route = path => routesRoot() + String(path || '').replace(/^\//, '');
  const overrideKey = source => `bpd-adhesive-override:${source}`;
  const suppressedKitKey = source => `bpd-suppressed-kit:${source}`;

  const cartSectionIds = () => Array.from(new Set(
    Array.from(document.querySelectorAll('hdt-cart-drawer[section-id], hdt-main-cart[section-id]'))
      .map(element => String(element.getAttribute('section-id') || '').trim())
      .filter(Boolean)
  ));

  const withRenderedSections = payload => {
    const ids = cartSectionIds();
    if (ids.length) {
      payload.sections = ids;
      payload.sections_url = window.location.pathname;
    }
    return payload;
  };

  const rememberRenderedSections = data => {
    if (data && data.sections && typeof data.sections === 'object') renderedSections = data.sections;
    if (window.BPDCartState) window.BPDCartState.invalidate();
    return data;
  };

  const applyRenderedSections = sections => {
    if (!sections || typeof sections !== 'object') return;
    Object.entries(sections).forEach(([sectionId, html]) => {
      if (!html) return;
      const current = Array.from(document.querySelectorAll('hdt-cart-drawer[section-id], hdt-main-cart[section-id]'))
        .find(element => String(element.getAttribute('section-id') || '') === String(sectionId));
      if (!current) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const tagName = current.tagName.toLowerCase();
      const fresh = Array.from(parsed.querySelectorAll(tagName))
        .find(element => String(element.getAttribute('section-id') || '') === String(sectionId)) || parsed.querySelector(tagName);
      if (!fresh) return;
      current.replaceChildren(...Array.from(fresh.childNodes).map(node => document.importNode(node, true)));
    });
    document.dispatchEvent(new CustomEvent('currency:update'));
  };

  const getCart = force => {
    if (window.BPDCartState && window.BPDCartState.get) return window.BPDCartState.get(!!force);
    return fetch(route('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(response => {
      if (!response.ok) throw new Error('Unable to read the basket.');
      return response.json();
    });
  };

  const changeItem = (id, quantity, properties) => {
    const body = withRenderedSections({ id, quantity });
    if (properties) body.properties = properties;
    return fetch(route('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(async response => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to update the basket.');
      return rememberRenderedSections(data);
    });
  };

  const addLine = (id, quantity, properties) => {
    const body = withRenderedSections({ items: [{ id: Number(id), quantity, properties }] });
    return fetch(route('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(async response => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to update the installation kit.');
      return rememberRenderedSections(data);
    });
  };

  const loadKitVariants = () => {
    if (!kitVariantsPromise) {
      kitVariantsPromise = fetch(route(`products/${KIT_HANDLE}.js`), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(response => {
        if (!response.ok) throw new Error('Unable to load installation kit variants.');
        return response.json();
      }).then(product => Array.isArray(product.variants) ? product.variants : []);
    }
    return kitVariantsPromise;
  };

  const kindOf = item => String(item && item.properties && item.properties[MANAGED] || '');
  const sourceOf = item => Number(item && item.properties && item.properties[SOURCE] || 0);
  const isPanel = item => String(item && item.product_type || '').trim().toLowerCase() === 'pvc panel';

  const panelQuantity = (cart, source) => (cart.items || []).reduce((sum, item) => {
    if (!isPanel(item) || Number(item.variant_id) !== Number(source)) return sum;
    return sum + Number(item.quantity || 0);
  }, 0);

  const kitColour = item => {
    const title = String(item && item.variant_title || '');
    const match = title.match(/\/\s*(White|Clear|Grey)\s*$/i);
    return match ? match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase() : '';
  };

  const rememberSuppressedKit = (source, colour) => {
    if (!source || !colour) return;
    try { sessionStorage.setItem(suppressedKitKey(source), colour); } catch (_) {}
  };

  const readSuppressedKit = source => {
    try { return sessionStorage.getItem(suppressedKitKey(source)) || ''; } catch (_) { return ''; }
  };

  const clearSuppressedKit = source => {
    try { sessionStorage.removeItem(suppressedKitKey(source)); } catch (_) {}
  };

  const suppressedSources = () => {
    const result = [];
    try {
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i) || '';
        if (!key.startsWith('bpd-suppressed-kit:')) continue;
        const source = Number(key.slice('bpd-suppressed-kit:'.length) || 0);
        if (source) result.push(source);
      }
    } catch (_) {}
    return result;
  };

  const notify = cart => {
    const sections = renderedSections;
    renderedSections = {};
    applyRenderedSections(sections);
    if (window.BPDCartState) window.BPDCartState.set(cart);
    const detail = {
      resource: { sections },
      cartData: cart,
      source: 'bpd-panel-addon-sync',
      data: {
        itemCount: Number(cart && cart.item_count || 0),
        sections
      }
    };
    document.dispatchEvent(new CustomEvent('bpd:cart-addon-synced', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:reload', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:count:update', {
      bubbles: true,
      detail: { ...detail, item_count: Number(cart && cart.item_count || 0) }
    }));
  };

  const markManualIntent = element => {
    const row = element && element.closest && element.closest('[data-bpd-managed-addon="adhesive"]');
    if (!row) return;
    const source = Number(row.dataset.bpdSourcePanelVariant || 0);
    if (!source) return;
    try { sessionStorage.setItem(overrideKey(source), '1'); } catch (_) {}
  };

  const hasPendingOverride = source => {
    try { return sessionStorage.getItem(overrideKey(source)) === '1'; } catch (_) { return false; }
  };

  const clearPendingOverride = source => {
    try { sessionStorage.removeItem(overrideKey(source)); } catch (_) {}
  };

  async function removeManagedAddonSafely(kind, source) {
    if (!source || (kind !== 'adhesive' && kind !== 'kit')) return false;
    const cart = await getCart();
    const targets = (cart.items || []).filter(item => kindOf(item) === kind && sourceOf(item) === source);
    if (!targets.length) return false;
    for (const item of targets) await changeItem(item.key, 0);
    notify(await getCart(true));
    return true;
  }

  const propertiesWithoutOverride = item => {
    const props = { ...(item.properties || {}) };
    delete props[OVERRIDE];
    return props;
  };

  async function useRecommended(button) {
    const source = Number(button.dataset.bpdSourcePanelVariant || 0);
    if (!source) return;
    button.disabled = true;
    try {
      clearPendingOverride(source);
      const cart = await getCart();
      const recommended = panelQuantity(cart, source) * 2;
      const item = (cart.items || []).find(entry => kindOf(entry) === 'adhesive' && sourceOf(entry) === source);
      if (!item || recommended < 1) return;
      await changeItem(item.key, recommended, propertiesWithoutOverride(item));
      notify(await getCart(true));
    } catch (error) {
      console.error('[BPD add-on sync] Unable to restore recommended adhesive quantity.', error);
    } finally {
      button.disabled = false;
    }
  }

  async function replaceKit(item, desiredTitle) {
    const variants = await loadKitVariants();
    const desired = variants.find(variant => variant.title === desiredTitle && variant.available !== false);
    if (!desired) {
      console.warn('[BPD add-on sync] Matching installation kit variant was not found:', desiredTitle);
      return false;
    }
    await changeItem(item.key, 0);
    await addLine(desired.id, 1, { ...(item.properties || {}) });
    return true;
  }

  async function restoreSuppressedKit(cart, source, colour) {
    const panels = panelQuantity(cart, source);
    if (panels < 1) {
      clearSuppressedKit(source);
      return false;
    }
    if (panels > 25) return false;

    const hasManagedKit = (cart.items || []).some(item => kindOf(item) === 'kit' && sourceOf(item) === source);
    if (hasManagedKit) {
      clearSuppressedKit(source);
      return false;
    }

    const hasManagedAdhesive = (cart.items || []).some(item => kindOf(item) === 'adhesive' && sourceOf(item) === source);
    if (hasManagedAdhesive) {
      clearSuppressedKit(source);
      return false;
    }

    const variants = await loadKitVariants();
    const desiredTitle = `${panels} Panel kit / ${colour}`;
    const desired = variants.find(variant => variant.title === desiredTitle && variant.available !== false);
    if (!desired) return false;

    await addLine(desired.id, 1, { [MANAGED]: 'kit', [SOURCE]: String(source) });
    clearSuppressedKit(source);
    return true;
  }

  async function syncManagedAddons() {
    if (syncing) {
      rerun = true;
      return;
    }

    syncing = true;
    rerun = false;
    let changed = false;

    try {
      const cart = await getCart();

      for (const item of (cart.items || [])) {
        const kind = kindOf(item);
        const source = sourceOf(item);
        if ((kind !== 'adhesive' && kind !== 'kit') || !source) continue;

        const panels = panelQuantity(cart, source);

        if (panels < 1) {
          if (kind === 'adhesive') clearPendingOverride(source);
          if (kind === 'kit') clearSuppressedKit(source);
          await changeItem(item.key, 0);
          changed = true;
          continue;
        }

        if (kind === 'adhesive') {
          const manual = String(item.properties && item.properties[OVERRIDE] || '') === '1';
          const pendingManual = hasPendingOverride(source);

          if (pendingManual && !manual) {
            await changeItem(item.key, Number(item.quantity || 0), {
              ...(item.properties || {}),
              [OVERRIDE]: '1'
            });
            clearPendingOverride(source);
            changed = true;
            continue;
          }

          if (!manual && !pendingManual) {
            const recommended = panels * 2;
            if (Number(item.quantity || 0) !== recommended) {
              await changeItem(item.key, recommended);
              changed = true;
            }
          }
          continue;
        }

        if (kind === 'kit') {
          if (panels > 25) {
            const colour = kitColour(item);
            if (colour) rememberSuppressedKit(source, colour);
            await changeItem(item.key, 0);
            changed = true;
            continue;
          }

          clearSuppressedKit(source);

          const colour = kitColour(item);
          const desiredTitle = colour ? `${panels} Panel kit / ${colour}` : '';

          if (desiredTitle && String(item.variant_title || '') !== desiredTitle) {
            if (await replaceKit(item, desiredTitle)) changed = true;
            continue;
          }

          if (Number(item.quantity || 0) !== 1) {
            await changeItem(item.key, 1);
            changed = true;
          }
        }
      }

      const suppressed = suppressedSources();
      if (suppressed.length) {
        const latestCart = changed ? await getCart(true) : cart;
        for (const source of suppressed) {
          const colour = readSuppressedKit(source);
          if (!colour) continue;
          if (await restoreSuppressedKit(latestCart, source, colour)) changed = true;
        }
      }

      if (changed) notify(await getCart(true));
    } catch (error) {
      console.error('[BPD add-on sync] Sync failed.', error);
    } finally {
      syncing = false;
      if (rerun) schedule(40);
    }
  }

  function schedule(delay = 100) {
    window.clearTimeout(timer);
    timer = window.setTimeout(syncManagedAddons, delay);
  }

  document.addEventListener('click', event => {
    const recommended = event.target.closest && event.target.closest('[data-bpd-use-recommended]');
    if (recommended) {
      event.preventDefault();
      event.stopPropagation();
      useRecommended(recommended);
      return;
    }

    const quantityControl = event.target.closest && event.target.closest('button[name="plus"], button[name="minus"], [data-quantity-plus], [data-quantity-minus]');
    if (quantityControl) {
      markManualIntent(quantityControl);
      schedule(650);
    }

    const removeControl = event.target.closest && event.target.closest('[href*="/cart/change"], .hdt-cart-item__remove');
    if (removeControl) {
      const managedRow = removeControl.closest('[data-bpd-managed-addon]');
      if (managedRow) {
        const source = Number(managedRow.dataset.bpdSourcePanelVariant || 0);
        const kind = String(managedRow.dataset.bpdManagedAddon || '');

        if (source && (kind === 'adhesive' || kind === 'kit')) {
          event.preventDefault();
          event.stopImmediatePropagation();

          if (kind === 'adhesive') clearPendingOverride(source);
          if (kind === 'kit') clearSuppressedKit(source);

          removeManagedAddonSafely(kind, source).catch(error => {
            console.error('[BPD add-on sync] Unable to remove managed add-on safely.', error);
            schedule(80);
          });
          return;
        }
      }
      schedule(650);
    }
  }, true);

  document.addEventListener('change', event => {
    if (event.target && event.target.matches && event.target.matches('input[name="updates[]"], .hdt-quantity-input')) {
      markManualIntent(event.target);
      schedule(650);
    }
  }, true);

  EVENT_NAMES.forEach(name => {
    document.addEventListener(name, event => {
      if (event && event.detail && event.detail.source === 'bpd-panel-addon-sync') return;
      schedule(60);
    });
  });

  window.addEventListener('pageshow', () => schedule(40));

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) schedule(60);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => schedule(40), { once: true });
  } else {
    schedule(40);
  }
})();